// Sopwith 3D - shared materials.
//
// Everything in the world uses toon shading with a three-step gradient and a
// dark inverted-hull outline, so objects read as flat cartoon cutouts and
// the terrain separates cleanly from the sky.

import * as THREE from 'three';

let gradientMap: THREE.DataTexture | null = null;

/** Three-band lighting ramp shared by all toon materials. */
export function toonGradient(): THREE.DataTexture {
  if (!gradientMap) {
    const data = new Uint8Array([90, 170, 255]);
    gradientMap = new THREE.DataTexture(data, data.length, 1, THREE.RedFormat);
    gradientMap.minFilter = THREE.NearestFilter;
    gradientMap.magFilter = THREE.NearestFilter;
    gradientMap.generateMipmaps = false;
    gradientMap.needsUpdate = true;
  }
  return gradientMap;
}

export function toon(color: THREE.ColorRepresentation): THREE.MeshToonMaterial {
  return new THREE.MeshToonMaterial({ color, gradientMap: toonGradient() });
}

export function toonVertexColors(): THREE.MeshToonMaterial {
  return new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: toonGradient() });
}

export const OUTLINE_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0x14181c,
  side: THREE.BackSide,
});

/**
 * Add an inverted-hull outline to every mesh under `root`: a slightly larger
 * back-face copy drawn in the outline colour. `thickness` is in world units.
 */
export function addOutlines(root: THREE.Object3D, thickness = 0.7): void {
  const meshes: THREE.Mesh[] = [];
  root.traverse((o) => {
    if ((o as THREE.Mesh).isMesh && o.name !== 'outline') {
      meshes.push(o as THREE.Mesh);
    }
  });
  for (const m of meshes) {
    const hull = new THREE.Mesh(m.geometry, OUTLINE_MATERIAL);
    hull.name = 'outline';
    // Scale so the hull extends `thickness` beyond the mesh on each axis.
    m.geometry.computeBoundingBox();
    const size = new THREE.Vector3();
    m.geometry.boundingBox!.getSize(size);
    hull.scale.set(
      1 + (2 * thickness) / Math.max(size.x, 0.1),
      1 + (2 * thickness) / Math.max(size.y, 0.1),
      1 + (2 * thickness) / Math.max(size.z, 0.1),
    );
    hull.renderOrder = -1;
    m.add(hull);
  }
}
